import { createContext, useCallback, useContext, useState, useEffect } from 'react';

/**
 * Paid add-on equipment in `bookingData.equipment`:
 * - Quantified: { name, count, totalPrice }
 * - Single toggle (e.g. max 1): { name, checked: true, totalPrice }
 *
 * `bookingData.bookedEquipment` mirrors a normalized snapshot for APIs / receipts.
 */

/** Round to 2 decimal places (cents) to avoid float artifacts (e.g. 26.700000000000003). */
export function roundEuros(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    return Math.round(n * 100) / 100;
}

export function computePaidEquipmentSubtotal(equipment) {
    if (!Array.isArray(equipment)) return 0;
    const raw = equipment.reduce((sum, item) => sum + (Number(item.totalPrice) || 0), 0);
    return roundEuros(raw);
}

/** Normalized lines for payloads / TotalBox (only lines with a positive total). */
export function deriveBookedEquipmentLines(equipment) {
    if (!Array.isArray(equipment)) return [];
    return equipment
        .filter((item) => (Number(item.totalPrice) || 0) > 0 && item?.equipmentItemId)
        .map((item) => {
            const qty =
                typeof item.count === "number" && item.count > 0
                    ? item.count
                    : item.checked
                      ? 1
                      : 0;
            return {
                equipmentItemId: item.equipmentItemId,
                name: item.name,
                quantity: qty,
                totalPrice: roundEuros(item.totalPrice),
            };
        });
}

/** Left column: Stück + name only, e.g. "2 x Sony Stativ" (always "n x …" when n ≥ 1). */
export function getPaidEquipmentPreisRowLabel(item) {
    if (!item?.name) return "";
    const qty =
        typeof item.count === "number" && item.count > 0
            ? item.count
            : item.checked
              ? 1
              : 0;
    if (qty >= 1) return `${qty} x ${item.name}`;
    return item.name;
}

/** Display euro string for breakdown (e.g. "20 €" / "26,70 €"). */
export function formatEquipmentPreisEuros(value) {
    const n = roundEuros(value);
    const cents = Math.round(Math.abs(n * 100)) % 100;
    if (cents === 0) return `${Math.round(n)} €`;
    return `${n.toFixed(2).replace(".", ",")} €`;
}

const BookingContext = createContext();

const STORAGE_KEY = 'studioBookingData_';
const EXPIRATION_TIME = 30 * 60 * 1000; // 30 minutes in milliseconds
const SERVER_PRICING_DEFAULTS = Object.freeze({
    serverTotalAmountCents: null,
    originalAmountCents: null,
    campaignDiscountAmountCents: 0,
    campaignApplied: false,
    serverPriceSynced: false,
});
const SENSITIVE_PERSISTED_BOOKING_KEYS = new Set([
    'paymentDraft',
    'reservation',
    'reservationToken',
    'clientSecret',
    'paymentIntentID',
    'paymentIntentId',
]);
const PRICE_RELEVANT_BOOKING_FIELDS = new Set([
    'date',
    'startTime',
    'endTime',
    'numberOfGuests',
    'numberOfPeople',
    'equipment',
    'bookedEquipment',
    'creativePackages',
    'packageId',
    'packageName',
    'packageDurationHours',
    'selectedPeopleGroupKey',
    'packageBasePrice',
    'packagePeoplePrice',
    'baseStudioPrice',
    'totalStudioPrice',
    'totalCreativePrice',
    'totalEquipmentPrice',
    'serviceFee',
    'totalPrice',
    'appliedDiscount',
    'discountMinHoursApplied',
    'discountTypeApplied',
    'discountValueApplied',
    'appliedSurcharge',
    'appliedSurchargeMeta',
    'appliedFees',
    'pricingUnavailable',
    'pricingUnavailableReason',
]);

export const createDefaultBookingData = (studioId) => ({
    studioId,
    date: '',
    startTime: '',
    endTime: '',
    addFreelancer: false,
    capacity: '',
    numberOfGuests: '',
    numberOfPeople: '1-5',
    equipment: [],
    // Equipment booking (for backend)
    bookedEquipment: [],
    // Studio package booking (for PACKAGES pricing mode)
    packageId: '',
    packageName: '',
    packageDurationHours: null,
    selectedPeopleGroupKey: null,
    packageBasePrice: 0, // Base package price (lowest people group price)
    packagePeoplePrice: 0, // Additional price for selected people group
    // Pricing breakdown
    baseStudioPrice: 0,
    totalStudioPrice: 0,
    totalCreativePrice: 0,
    totalEquipmentPrice: 0,
    serviceFee: 0,
    totalPrice: 0,
    // Price calculation details
    appliedDiscount: 0,
    discountMinHoursApplied: null,
    discountTypeApplied: null,
    discountValueApplied: null,
    appliedSurcharge: 0,
    appliedSurchargeMeta: null,
    appliedFees: [],
    pricingUnavailable: false,
    pricingUnavailableReason: null,
    // Server-authoritative price returned by createBookingSecure.
    ...SERVER_PRICING_DEFAULTS,
    // Project / booking meta
    projectName: '',
    companyName: '',
    description: '',
    id: '',
    paymentDraft: null,
    // Creative packages attached to this booking (BookedCreativePackage[])
    // Shape per schema:
    // {
    //   creativeId: string,
    //   packageId: string,
    //   duration: number,
    //   price: number,
    //   specialRequests: string
    // }
    creativePackages: [],
});

const arrayOrDefault = (value, defaultValue) => (
    Array.isArray(value) ? value : defaultValue
);

const finiteNumberOrDefault = (value, defaultValue) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : defaultValue;
};

const finiteIntegerOrDefault = (value, defaultValue) => {
    if (value === null || value === undefined || value === '') return defaultValue;
    const n = Number(value);
    return Number.isFinite(n) ? Math.round(n) : defaultValue;
};

const booleanOrDefault = (value, defaultValue) => (
    typeof value === 'boolean' ? value : defaultValue
);

const shouldResetServerPricing = (step, previousValue, nextValue) => (
    PRICE_RELEVANT_BOOKING_FIELDS.has(step) && !Object.is(previousValue, nextValue)
);

const normalizeBookingData = (data, studioId) => {
    const defaults = createDefaultBookingData(studioId);
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return defaults;
    }

    const next = {
        ...defaults,
        ...data,
        studioId,
        equipment: arrayOrDefault(data.equipment, defaults.equipment),
        bookedEquipment: arrayOrDefault(data.bookedEquipment, defaults.bookedEquipment),
        creativePackages: arrayOrDefault(data.creativePackages, defaults.creativePackages),
        appliedFees: arrayOrDefault(data.appliedFees, defaults.appliedFees),
    };

    [
        'packageBasePrice',
        'packagePeoplePrice',
        'baseStudioPrice',
        'totalStudioPrice',
        'totalCreativePrice',
        'totalEquipmentPrice',
        'serviceFee',
        'totalPrice',
        'appliedDiscount',
        'appliedSurcharge',
    ].forEach((key) => {
        next[key] = finiteNumberOrDefault(next[key], defaults[key]);
    });

    [
        'serverTotalAmountCents',
        'originalAmountCents',
        'campaignDiscountAmountCents',
    ].forEach((key) => {
        next[key] = finiteIntegerOrDefault(next[key], defaults[key]);
    });
    next.campaignApplied = booleanOrDefault(next.campaignApplied, defaults.campaignApplied);
    next.serverPriceSynced = booleanOrDefault(next.serverPriceSynced, defaults.serverPriceSynced);
    next.pricingUnavailable = booleanOrDefault(next.pricingUnavailable, defaults.pricingUnavailable);
    next.pricingUnavailableReason =
        typeof next.pricingUnavailableReason === 'string'
            ? next.pricingUnavailableReason
            : defaults.pricingUnavailableReason;

    const duration = Number(next.packageDurationHours);
    next.packageDurationHours = Number.isFinite(duration) && duration > 0 ? duration : null;

    if (!next.date) {
        next.startTime = '';
        next.endTime = '';
    }

    return next;
};

const stripSensitivePersistedBookingValues = (value) => {
    if (Array.isArray(value)) {
        return value.map(stripSensitivePersistedBookingValues);
    }

    if (!value || typeof value !== 'object') {
        return value;
    }

    return Object.entries(value).reduce((safeValue, [key, entryValue]) => {
        if (SENSITIVE_PERSISTED_BOOKING_KEYS.has(key)) {
            return safeValue;
        }

        safeValue[key] = stripSensitivePersistedBookingValues(entryValue);
        return safeValue;
    }, {});
};

export const createPersistableBookingData = (bookingData) => (
    stripSensitivePersistedBookingValues({
        ...bookingData,
        paymentDraft: null,
    })
);

export const sanitizeLegacyBookingDataStorage = () => {
    if (typeof localStorage === 'undefined') return;

    try {
        for (let index = localStorage.length - 1; index >= 0; index -= 1) {
            const storageKey = localStorage.key(index);
            if (!storageKey?.startsWith(STORAGE_KEY)) continue;

            try {
                const savedData = localStorage.getItem(storageKey);
                if (!savedData) continue;

                const { data, timestamp } = JSON.parse(savedData);
                if (!(Date.now() - timestamp < EXPIRATION_TIME)) {
                    localStorage.removeItem(storageKey);
                    continue;
                }

                const studioId = storageKey.slice(STORAGE_KEY.length);
                const normalizedData = normalizeBookingData(data, studioId);
                localStorage.setItem(storageKey, JSON.stringify({
                    data: createPersistableBookingData(normalizedData),
                    timestamp,
                }));
            } catch (error) {
                localStorage.removeItem(storageKey);
            }
        }
    } catch (error) {
        // Ignore storage failures; individual providers still sanitize on load.
    }
};

const loadStoredBookingData = (storageKey, studioId) => {
    try {
        const savedData = localStorage.getItem(storageKey);
        if (!savedData) {
            return createDefaultBookingData(studioId);
        }

        const { data, timestamp } = JSON.parse(savedData);
        if (Date.now() - timestamp < EXPIRATION_TIME) {
            const normalizedData = normalizeBookingData(data, studioId);
            const sanitizedData = createPersistableBookingData(normalizedData);
            try {
                localStorage.setItem(storageKey, JSON.stringify({
                    data: sanitizedData,
                    timestamp,
                }));
            } catch (error) {
                // Ignore migration failures; future writes will try again.
            }
            return normalizeBookingData(sanitizedData, studioId);
        }

        localStorage.removeItem(storageKey);
    } catch (error) {
        localStorage.removeItem(storageKey);
    }

    return createDefaultBookingData(studioId);
};

export const BookingProvider = ({ children, studioId }) => {
    const storageKey = `${STORAGE_KEY}${studioId}`;

    const [bookingData, setBookingData] = useState(() => loadStoredBookingData(storageKey, studioId));

    useEffect(() => {
        setBookingData(loadStoredBookingData(storageKey, studioId));
    }, [storageKey, studioId]);

    useEffect(() => {
        if (bookingData?.studioId !== studioId) return;
        // Store data with timestamp
        const dataToStore = {
            data: createPersistableBookingData(bookingData),
            timestamp: Date.now()
        };
        try {
            localStorage.setItem(storageKey, JSON.stringify(dataToStore));
        } catch (error) {
            // Ignore storage failures; booking state remains available in memory.
        }
    }, [bookingData, storageKey, studioId]);

    const updateBookingData = useCallback((step, data) => {
        setBookingData(prev => {
            const next = {
                ...prev,
                [step]: data,
            };

            if (step === 'date' && !data) {
                next.startTime = '';
                next.endTime = '';
            }

            if (shouldResetServerPricing(step, prev?.[step], data)) {
                return {
                    ...next,
                    ...SERVER_PRICING_DEFAULTS,
                };
            }

            return next;
        });
    }, []);

    const clearBookingData = useCallback(() => {
        setBookingData(createDefaultBookingData(studioId));
        try {
            localStorage.removeItem(storageKey);
        } catch (error) {
            // Ignore storage failures; booking state remains available in memory.
        }
    }, [storageKey, studioId]);
    
    return (
        <BookingContext.Provider value={{
            bookingData,
            updateBookingData,
            clearBookingData
        }}>
            {children}
        </BookingContext.Provider>
    );
};

export const useBooking = (studioId) => {
    const context = useContext(BookingContext);
    if (!context) {
        throw new Error('useBooking must be used within a BookingProvider');
    }
    return context;
};
